import { useState } from 'react'
import { Link } from 'react-router-dom'
import { FiHeart, FiBell, FiArrowRight } from 'react-icons/fi'
import { HiCheckBadge } from 'react-icons/hi2'
import { FaStar } from 'react-icons/fa6'
import { useToast } from '../../../context/ToastContext'

function Stars({ rating, reviewCount }) {
  const full = Math.floor(rating)
  const stars = Array.from({ length: 5 }, (_, i) => i < full)
  return (
    <div className="flex items-center gap-1 mb-3">
      {stars.map((filled, i) => (
        <FaStar key={i} className="w-3 h-3" style={{ color: filled ? '#C8922A' : '#E8DFCF' }} />
      ))}
      <span className="text-[9px] font-bold ml-1" style={{ color: '#C8922A' }}>
        {reviewCount > 0 ? rating.toFixed(1) : 'New'}
      </span>
    </div>
  )
}

export default function AstrologerCard({ astro, liveRating }) {
  const [isFav, setIsFav] = useState(false)
  const { showToast } = useToast()

  const isBusy = astro.status === 'busy'

  const liveResolved = liveRating && !liveRating.isPending
  const rating = liveResolved ? liveRating.averageRating : astro.rating
  const reviewCount = liveResolved ? liveRating.totalReviews : astro.reviews
  const featured = liveResolved ? reviewCount > 0 && rating >= 4.5 : astro.featured

  return (
    <div className="astro-pro-card group">
      {/* Stretched link — makes the whole card open the profile. Interactive
          controls (favourite, Book CTA) sit above it via higher z-index. */}
      <Link
        to={`/astrologers/${astro.id}`}
        className="absolute inset-0 z-1"
        aria-label={`View ${astro.name}'s profile`}
      />
      <div className="astro-pro-photo">
        <div className="absolute inset-0 flex items-center justify-center font-cinzel text-6xl font-bold" style={{ background: astro.fallbackGradient, color: 'rgba(120,50,0,.5)' }}>
          {astro.fallbackLetter}
        </div>
        {astro.image && (
          <img src={astro.image} alt={astro.name} loading="lazy" onError={(e) => { e.currentTarget.style.opacity = '0' }} />
        )}
        {featured && <div className="feat-ribbon">Top Rated</div>}

        <div className="absolute top-3 inset-x-3 flex items-center justify-between z-10">
          {isBusy ? (
            <div className="s-busy flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[8px] font-bold uppercase tracking-wider">
              <div className="busy-blink w-1.5 h-1.5 rounded-full" style={{ background: '#D97706' }} />
              Busy
            </div>
          ) : (
            <div className="s-live flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[8px] font-bold uppercase tracking-wider">
              <div className="relative w-1.5 h-1.5">
                <div className="live-ring absolute inset-0 rounded-full" style={{ background: 'rgba(34,197,94,.4)' }} />
                <div className="w-1.5 h-1.5 rounded-full" style={{ background: '#22c55e' }} />
              </div>
              Live
            </div>
          )}
          <button
            type="button"
            className={`fav-btn${isFav ? ' is-fav' : ''}`}
            onClick={() => setIsFav((v) => !v)}
            aria-label="Add to favourites"
          >
            <FiHeart className="fav-ico w-3.5 h-3.5" style={{ color: isFav ? undefined : '#8B7355' }} />
          </button>
        </div>

        <div className="absolute top-12 left-3 z-10 flex items-center gap-1 px-2 py-0.5 rounded-full text-[7px] font-bold" style={{ background: 'rgba(255,255,255,.88)', backdropFilter: 'blur(8px)', color: '#15803D' }}>
          <HiCheckBadge className="w-2.5 h-2.5" />
          Verified
        </div>

        <div className="absolute inset-x-0 bottom-0 z-10">
          <span className="inline-flex items-center gap-1 text-[7.5px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full" style={{ ...astro.tagColor, backdropFilter: 'blur(8px)' }}>
            {astro.tag}
          </span>
        </div>
      </div>

      <div className="astro-card-body">
        <h3 className="font-cinzel text-[20px] font-bold leading-tight mb-0.5 text-[#1C0A03] transition-colors group-hover:text-secondary">
          {astro.name}
        </h3>
        <p className="text-[9px] uppercase tracking-[.18em] mb-3" style={{ color: '#9A7A58' }}>{astro.role}</p>

        <div className="grid grid-cols-3 gap-1.5 mb-3">
          <div className="flex flex-col items-center py-2 rounded-xl" style={{ background: 'rgba(200,146,42,.06)', border: '1px solid rgba(200,146,42,.1)' }}>
            <span className="font-cinzel text-[12px] font-bold leading-tight" style={{ color: '#1C0A03' }}>{astro.exp}</span>
            <span className="text-[6px] uppercase tracking-wide mt-0.5" style={{ color: '#9A7A58' }}>Exp</span>
          </div>
          <div className="flex flex-col items-center py-2 rounded-xl" style={{ background: 'rgba(200,146,42,.06)', border: '1px solid rgba(200,146,42,.1)' }}>
            <span className="font-cinzel text-[12px] font-bold leading-tight" style={{ color: '#1C0A03' }}>{astro.duration}</span>
            <span className="text-[6px] uppercase tracking-wide mt-0.5" style={{ color: '#9A7A58' }}>Session</span>
          </div>
          <div className="flex flex-col items-center py-2 rounded-xl" style={{ background: 'rgba(200,146,42,.06)', border: '1px solid rgba(200,146,42,.1)' }}>
            <span className="font-cinzel text-[12px] font-bold leading-tight inline-flex items-center gap-0.5" style={{ color: '#C8922A' }}>
              {reviewCount > 0 ? rating.toFixed(1) : 'New'}
              <FaStar className="w-2.5 h-2.5" />
            </span>
            <span className="text-[6px] uppercase tracking-wide mt-0.5" style={{ color: '#9A7A58' }}>Rating</span>
          </div>
        </div>

        <div className="flex items-center gap-1 mb-3">
          <Stars rating={rating} reviewCount={reviewCount} />
          <span className="text-[8px] -ml-1" style={{ color: '#A08870' }}>({reviewCount} review{reviewCount === 1 ? '' : 's'})</span>
        </div>

        {astro.languages.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {astro.languages.map((lang) => (
              <span key={lang} className="meta-pill">{lang}</span>
            ))}
          </div>
        )}

        <div className="astro-divider mb-3.5" />

        <div className="flex items-end justify-between">
          <div>
            <div className="font-cinzel text-[22px] font-bold leading-tight" style={{ color: '#C8922A' }}>{astro.price}</div>
            <div className="text-[7px] uppercase tracking-widest" style={{ color: '#9A7A58' }}>per session</div>
          </div>
          {isBusy ? (
            <button
              type="button"
              className="relative z-2 flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-[9px] font-bold uppercase tracking-widest transition-all"
              style={{ background: 'linear-gradient(135deg,#3A1A00,#5A2E00)', color: '#FDE68A', boxShadow: '0 6px 18px -4px rgba(58,26,0,.4)' }}
              onClick={() => showToast(`We will notify you when ${astro.name} is available.`)}
            >
              {astro.cta}
              <FiBell className="w-3 h-3" />
            </button>
          ) : (
            <Link
              to={`/astrologers/${astro.id}/book`}
              className="relative z-2 flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-[9px] font-bold uppercase tracking-widest transition-all"
              style={{ background: 'linear-gradient(135deg,#1C0A03,#2E1206)', color: '#E5A820', boxShadow: '0 6px 18px -4px rgba(28,10,3,.35)' }}
            >
              {astro.cta}
              <FiArrowRight className="w-3 h-3" />
            </Link>
          )}
        </div>
      </div>
    </div>
  )
}
